// Parked bills (v4 board fx-parked): the list on the left, the picked bill on the
// right with its items, totals and actions. Retrieving goes through Core's
// retrieveParkedData (park-current-bill prompt, stock check, approvals all
// unchanged). With retrieveTo="cart" the primary action puts the bill back on
// the cart and leaves payment closed; "Retrieve and pay" is today's behaviour.
// Without it there is one "Retrieve" button, as before. With floorBills, bills
// sent from the phone floor sale are listed first.
// Discard asks first, then runs Core's handleOk (the same handler behind Core's
// confirm, approvals included); without it, Core's dialog is used. The reason
// picked is only shown to the cashier: Core's discard has no field to keep it.
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { formatMoney } from "../../../../utils/fashion/money";
import { billTotals } from "../../../../utils/fashion/billTotals";
import { billLineViews, idsNeedingVariant, loadVariantLabels } from "../../../../utils/fashion/billLines";
import { canRetrieveToCart, retrieveParked } from "../../../../utils/fashion/parkedRetrieve";
import FloorBillsSection from "./FloorBillsSection";
import EmptyState from "../EmptyState";

export const DISCARD_REASONS = ["Customer left without buying", "Duplicate of another bill", "Items returned to shelf"];

const customerOf = (bill) => (bill?.parkedCart?.customer && typeof bill.parkedCart.customer === "object" ? bill.parkedCart.customer : null);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const activeItems = (items) => (items || []).filter((item) => item && item.isCanceled !== "Y" && Number(item.weight ?? item.qty) !== 0);

function minutesSince(value) {
  const d = value ? dayjs(value) : null;
  return d && d.isValid() ? Math.max(0, dayjs().diff(d, "minute")) : null;
}

// "12 min ago", "2 h 10 min ago", "3 days ago".
export function parkedAgo(value) {
  const mins = minutesSince(value);
  if (mins == null) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) {
    const m = mins % 60;
    return `${Math.floor(mins / 60)} h${m ? ` ${m} min` : ""} ago`;
  }
  const days = Math.floor(mins / (24 * 60));
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

function parkedAt(value) {
  const d = value ? dayjs(value) : null;
  if (!d || !d.isValid()) return "";
  return d.isSame(dayjs(), "day") ? d.format("HH:mm") : d.format("DD MMM, HH:mm");
}

// Every parked bill carries a "Held" pill; past 2 hours it turns amber so the
// cashier knows the customer may have left.
function heldPill(value) {
  const mins = minutesSince(value);
  if (mins != null && mins >= 120) return { cls: "is-warn", text: "Held over 2 h" };
  return { cls: "is-info", text: "Held" };
}

const itemsLabel = (n) => `${n} ${Number(n) === 1 ? "item" : "items"}`;

// Item rows for the detail table: name, SKU, colour · size, qty, unit price, discount, amount.
function detailLines(items, variants) {
  return activeItems(items).map((item, i) => {
    const view = billLineViews([item], variants)[0];
    const qty = view.qty || 1;
    const discount = num(item.discount);
    const gross = view.amount + discount;
    return {
      ...view,
      key: `${view.key}-${i}`,
      sku: item.sku || item.value || "",
      price: gross / qty,
      discount,
    };
  });
}

export default function ParkedBillsView({
  parkedList,
  tillData,
  retrieveParkedData,
  discardMessage,
  handleOk,
  setShowPaymentMethods,
  onDone,
  retrieveTo = "payment",
  customerLabel,
  floorBills = false,
  online = true,
  cart,
  setCart,
}) {
  const [term, setTerm] = useState("");
  const [variants, setVariants] = useState({});
  const [floorItems, setFloorItems] = useState([]);
  const [pickedKey, setPickedKey] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const bills = useMemo(() => (parkedList || []).filter((bill) => bill?.parkedCart), [parkedList]);
  const showFloor = floorBills && typeof setCart === "function";
  const money = (v) => formatMoney(v, tillData);

  useEffect(() => {
    let live = true;
    const ids = idsNeedingVariant(bills.flatMap((bill) => bill.parkedCart.items || []).concat(floorItems)).filter((id) => !(id in variants));
    if (ids.length === 0) return undefined;
    loadVariantLabels(ids).then((found) => live && setVariants((prev) => ({ ...prev, ...found })));
    return () => {
      live = false;
    };
  }, [bills, floorItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const q = term.trim().toLowerCase();
    const qd = q.replace(/\D/g, "");
    if (!q) return bills;
    return bills.filter((bill) => {
      const c = customerOf(bill);
      return (
        String(bill.parkedDocNo || "").toLowerCase().includes(q) ||
        String(c?.name || "").toLowerCase().includes(q) ||
        (qd.length >= 3 && String(c?.mobileNo || "").replace(/\D/g, "").includes(qd)) ||
        (bill.parkedCart.items || []).some((i) => String(i?.name || "").toLowerCase().includes(q))
      );
    });
  }, [bills, term]);

  const keyOf = (bill) => bill.parkedBillId || bill.parkedDocNo;
  // The picked bill stays picked while it's in the filtered list; otherwise the first one shows.
  const picked = rows.find((bill) => keyOf(bill) === pickedKey) || rows[0] || null;
  const toCart = retrieveTo === "cart" && canRetrieveToCart({ retrieveParkedData, setShowPaymentMethods });

  const pick = (bill) => {
    setPickedKey(keyOf(bill));
    setConfirm(null);
  };
  const askDiscard = (bill) => {
    if (typeof handleOk === "function") setConfirm({ key: keyOf(bill), reason: DISCARD_REASONS[0] });
    else discardMessage("Discard Sale", "Are you sure you want to discard the sale!", bill);
  };
  const discard = (bill) => {
    setConfirm(null);
    handleOk(bill);
  };
  const retrieve = (bill, to) => {
    retrieveParked(bill, { retrieveTo: to, retrieveParkedData, setShowPaymentMethods });
    onDone?.();
  };

  const qtyOf = (bill) => {
    const c = bill.parkedCart;
    return c.totalQty ?? bill.qty ?? activeItems(c.items).reduce((a, i) => a + Math.abs(num(i.weight ?? i.qty)), 0);
  };

  const renderDetail = (bill) => {
    const c = customerOf(bill);
    const pcart = bill.parkedCart;
    const lines = detailLines(pcart.items, variants);
    const totals = billTotals(pcart);
    const gst = totals.gst.reduce((a, g) => a + g.amount, 0);
    const discounts = totals.offerDiscount + totals.billDiscount;
    const held = heldPill(bill.parkedTime);
    const docNo = bill.parkedDocNo || "this bill";
    const note = pcart.parkNote || pcart.note || bill.note || "";
    const sub = [customerLabel(pcart.customer), c?.mobileNo, bill.parkedTime && `parked ${parkedAt(bill.parkedTime)}`, note && `note: ${note}`].filter(Boolean).join(" · ");
    const asking = confirm?.key === keyOf(bill);
    return (
      <article className="fp-pk-detail" aria-label={`Parked bill ${bill.parkedDocNo || ""}`}>
        <header className="fp-pk-dhead">
          <span className="fp-pk-dtitle">
            <h3 className="fp-num">{bill.parkedDocNo || "Parked bill"}</h3>
            <span className="fp-bills-meta fp-num">{sub}</span>
          </span>
          <span className={`fp-bills-pill ${held.cls}`}>{held.text}</span>
        </header>
        <div className="fp-pk-dscroll">
          <span className="fp-bills-lbl">Items</span>
          {lines.length === 0 ? (
            <p className="fp-bills-meta">No items on this bill.</p>
          ) : (
            <table className="fp-bills-tbl fp-pk-tbl" aria-label="Items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Size · colour</th>
                  <th className="is-r">Qty</th>
                  <th className="is-r">Price</th>
                  <th className="is-r">Discount</th>
                  <th className="is-r">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.key}>
                    <td>
                      {l.name}
                      {l.sku && <span className="fp-bills-meta fp-num">{l.sku}</span>}
                    </td>
                    <td>{l.variant || "—"}</td>
                    <td className="is-r fp-num">{l.qty}</td>
                    <td className="is-r fp-num">{money(l.price)}</td>
                    <td className="is-r fp-num">{l.discount ? `−${money(l.discount)}` : "—"}</td>
                    <td className="is-r fp-num">{money(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <dl className="fp-pk-totals">
            <div>
              <dt>Items</dt>
              <dd className="fp-num">{money(totals.items)}</dd>
            </div>
            {discounts !== 0 && (
              <div>
                <dt>Discounts</dt>
                <dd className="fp-num">−{money(discounts)}</dd>
              </div>
            )}
            {gst !== 0 && (
              <div>
                <dt>GST included</dt>
                <dd className="fp-num">{money(gst)}</dd>
              </div>
            )}
            <div className="is-total">
              <dt>Total</dt>
              <dd className="fp-num">{money(pcart.total)}</dd>
            </div>
          </dl>
        </div>
        <footer className="fp-pk-acts">
          {discardMessage && (
            <button type="button" className="fp-btn-ghost fp-btn-sm fp-bills-discard" onClick={() => askDiscard(bill)}>
              Discard bill…
            </button>
          )}
          <span className="fp-bills-acts">
            {retrieveParkedData && toCart && (
              <>
                <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => retrieve(bill, "payment")}>
                  Retrieve and pay
                </button>
                <button type="button" className="fp-bills-pri" onClick={() => retrieve(bill, "cart")}>
                  Retrieve to cart
                </button>
              </>
            )}
            {retrieveParkedData && !toCart && (
              <button type="button" className="fp-bills-pri" onClick={() => retrieve(bill, "payment")}>
                Retrieve
              </button>
            )}
          </span>
        </footer>
        {asking && (
          <div className="fp-pk-scrim">
            <div className="fp-pk-ask" role="group" aria-label={`Discard ${docNo}?`}>
              <h3>Discard {docNo}?</h3>
              <p className="fp-bills-meta">
                {customerLabel(pcart.customer)}&rsquo;s bill of {itemsLabel(qtyOf(bill))} ({money(pcart.total)}) will be removed from this till. This can&rsquo;t be undone.
              </p>
              <label className="fp-bills-field">
                <span className="fp-bills-lbl">Reason</span>
                <select className="fp-input" value={confirm.reason} onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })}>
                  {DISCARD_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <span className="fp-bills-acts">
                <button type="button" className="fp-btn-ghost fp-btn-sm" autoFocus onClick={() => setConfirm(null)}>
                  Keep bill
                </button>
                <button type="button" className="fp-bills-danger" onClick={() => discard(bill)}>
                  Discard bill
                </button>
              </span>
            </div>
          </div>
        )}
      </article>
    );
  };

  return (
    <div className="fp-bills-parked fp-pk">
      <div className="fp-pk-list">
        <input className="fp-input fp-panel-search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Find a parked bill" aria-label="Search parked bills" />
        <div className="fp-bills-scroll">
          {showFloor && (
            <FloorBillsSection
              cart={cart}
              setCart={setCart}
              tillData={tillData}
              online={online}
              customerLabel={customerLabel}
              variants={variants}
              onBills={(list) => setFloorItems(list.flatMap((b) => b.cart.items || []))}
              onDone={onDone}
            />
          )}
          {showFloor && <b className="fp-bills-floor-head">On this till</b>}
          {rows.length === 0 &&
            (term ? (
              <EmptyState icon="search" title={`No parked bill matches “${term}”.`} hint="Check the spelling, or try another search." />
            ) : (
              <EmptyState title="No parked bills on this till." hint="Park a bill from the cart to serve the next customer; it waits here." />
            ))}
          {rows.length > 0 && (
            <ul className="fp-pk-rows" aria-label="Parked bills">
              {rows.map((bill) => {
                const held = heldPill(bill.parkedTime);
                const on = picked && keyOf(picked) === keyOf(bill);
                const meta = [bill.parkedDocNo, itemsLabel(qtyOf(bill)), parkedAgo(bill.parkedTime)].filter(Boolean).join(" · ");
                return (
                  <li key={keyOf(bill)}>
                    <button type="button" className={`fp-pk-row${on ? " is-on" : ""}`} aria-pressed={on} onClick={() => pick(bill)}>
                      <span className="fp-pk-row-top">
                        <b>{customerLabel(bill.parkedCart.customer)}</b>
                        <b className="fp-num">{money(bill.parkedCart.total)}</b>
                      </span>
                      <span className="fp-pk-row-bot">
                        <span className="fp-bills-meta fp-num">{meta}</span>
                        <span className={`fp-bills-pill ${held.cls}`}>{held.text}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {bills.length > 0 && (
            <span className="fp-bills-meta fp-pk-count">
              Showing {rows.length} of {bills.length}
            </span>
          )}
        </div>
      </div>
      <div className="fp-pk-pane">{picked ? renderDetail(picked) : <EmptyState title="Pick a parked bill" hint="Its items and actions show here." />}</div>
    </div>
  );
}
